import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CategorySelector } from '@/components/category-selector';
import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  CategoryTree,
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { cn } from '@/lib/utils';
import {
  findCategoryById,
  formatCategoryText,
} from '@/lib/utils/category-utils';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import {
  isDealItemCategoryUpdating,
  resolveDealItemFromMap,
} from '@/lib/utils/updating-fields';
import { categoryTreeState } from '@/states/category-tree-map.state';
import { useSetColumnSort } from '@/states/column-sort.state';
import { productsMapState } from '@/states/products.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { Loader2 } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

type CategoryProductSelectorProps = {
  dealId: string;
  dealItemId: string;
  productCategoryId: string | null;
  isEmpty: boolean;
  isOfferLocked: boolean;
  isParent?: boolean;
  displayValue?: string | null;
  onOpenChange?: (open: boolean) => void;
};

const CategoryProductSelector = ({
  dealId,
  dealItemId,
  productCategoryId,
  isEmpty,
  isOfferLocked,
  isParent = false,
  displayValue,
  onOpenChange,
}: CategoryProductSelectorProps) => {
  const { t } = useTranslation();
  const { updateProduct } = useUpdateDealItems(dealId);

  const handleCategoryChange = useCallback(
    (category: CategoryTree) => {
      if (isOfferLocked || isParent) return;
      updateProduct({
        productCategoryId: category.id,
        id: dealItemId,
      });
    },
    [dealItemId, updateProduct, isOfferLocked, isParent]
  );

  // For parent rows, show the aggregated value or error state
  if (isParent) {
    return (
      <div
        className={cn(
          'flex w-full items-center justify-between space-x-2 truncate rounded p-2',
          'cursor-pointer opacity-50'
        )}
      >
        {isEmpty ? (
          <span className="text-muted-foreground">
            {t('kpi.type_required')}
          </span>
        ) : (
          <span className="truncate">{displayValue || '—'}</span>
        )}
      </div>
    );
  }

  return (
    <CategorySelector
      value={productCategoryId}
      onValueChange={handleCategoryChange}
      placeholder={isEmpty ? t('kpi.type_required') : t('kpi.type_placeholder')}
      emptyContent={undefined}
      disabled={isOfferLocked}
      skipTooltipProvider={true}
      onOpenChange={onOpenChange}
    />
  );
};

interface ColumnProps {
  dealId: string;
  filterFn?: (
    row: Row<DealItemWithVersions>,
    columnId: string,
    filterValue: unknown
  ) => boolean;
}

type ComponentProps = {
  isLoading: boolean;
  dealId: string;
  dealItemId: string;
  productCategoryId: string | null;
  isOfferLocked: boolean;
  isParent: boolean;
  displayValue?: string | null;
  isEmpty: boolean;
  categoryTree: CategoryTree[];
  isRecentlyModified: boolean;
};

const Component: React.FC<ComponentProps> = ({
  isLoading,
  dealId,
  dealItemId,
  productCategoryId,
  isOfferLocked,
  isParent,
  displayValue,
  isEmpty,
  isRecentlyModified,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading)
    return (
      <div className="flex w-full items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
      </div>
    );

  return (
    <div
      className={cn(
        'h-full w-full',
        isEmpty
          ? 'border-l-2 border-red-500 bg-red-50'
          : !isParent && isRecentlyModified
            ? 'border-l-2 border-blue-500 bg-blue-50'
            : '',
        isOpen && isEmpty
          ? 'border-red-500 ring-1 ring-inset ring-red-500'
          : isOpen
            ? 'border-black ring-1 ring-inset ring-black'
            : ''
      )}
    >
      <CategoryProductSelector
        dealId={dealId}
        dealItemId={dealItemId}
        productCategoryId={productCategoryId}
        isEmpty={isEmpty}
        isOfferLocked={isOfferLocked}
        isParent={isParent}
        displayValue={displayValue}
        onOpenChange={setIsOpen}
      />
    </div>
  );
};

const MemoizedComponent = memo(Component);

type CategoryCellProps = {
  row: Row<DealItemWithVersions>;
  dealId: string;
  isOfferLocked: boolean;
  categoryTree: CategoryTree[];
  displayValue: string;
  productCategoryId: string | null;
  isEmpty: boolean;
  isParent: boolean;
};

function CategoryCell({
  row,
  dealId,
  isOfferLocked,
  categoryTree,
  displayValue,
  productCategoryId,
  isEmpty,
  isParent,
}: CategoryCellProps) {
  const productsMap = useAtomValue(productsMapState);
  const isLoading = isDealItemCategoryUpdating(
    resolveDealItemFromMap(productsMap, row.original)
  );
  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="productCategory"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <MemoizedComponent
          isLoading={isLoading}
          dealId={dealId}
          dealItemId={row.original.id}
          productCategoryId={productCategoryId}
          isOfferLocked={isOfferLocked}
          isParent={isParent}
          displayValue={displayValue}
          isEmpty={isEmpty}
          categoryTree={categoryTree}
          isRecentlyModified={isRecentlyModified}
        />
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefType = ({
  dealId,
  filterFn,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const isOfferLocked = useIsOfferLocked();
  const { setColumnSort } = useSetColumnSort();
  // Read categoryTree once for sorting function (sorting happens on initial load)
  const categoryTreeForSorting = useAtomValue(categoryTreeState);

  const accessorKey: keyof DealItem = 'productCategory';

  const getValue = (
    row: Row<DealItemWithVersions>,
    categoryTree: CategoryTree[]
  ) => {
    const categoryId =
      row.original.activeVersion.productCategory?.productCategoryId ?? null;
    if (!categoryId) return '—';
    const category = findCategoryById(categoryTree, categoryId);
    return category ? formatCategoryText(category.label) : '—';
  };

  const getParentValue = (
    row: Row<DealItemWithVersions>,
    categoryTree: CategoryTree[]
  ) => {
    const subRows = row.subRows;
    if (!subRows || subRows.length === 0) {
      return '—';
    }

    const firstCategoryId =
      subRows[0]?.original.activeVersion.productCategory?.productCategoryId;
    if (!firstCategoryId) {
      return '—';
    }

    const allSameCategory = subRows.every(
      (subRow) =>
        subRow.original.activeVersion.productCategory?.productCategoryId ===
        firstCategoryId
    );

    if (allSameCategory) {
      const category = findCategoryById(categoryTree, firstCategoryId);
      return category ? formatCategoryText(category.label) : '—';
    }

    return '≠';
  };

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'Type',
      filterFn: filterFn,
      sortingFn: createSubRowSortingFn('productCategory', (row) => {
        const categoryId =
          row.activeVersion?.productCategory?.productCategoryId;
        if (!categoryId) return null;
        const category = findCategoryById(categoryTreeForSorting, categoryId);
        return category ? formatCategoryText(category.label) : null;
      }),
      header: () => (
        <div
          className="flex items-center"
          onClick={() => setColumnSort(accessorKey)}
        >
          {t('kpi.type')} <SortIcon pointer="productCategory" />
        </div>
      ),
      cell: ({ row }) => {
        const isParent = row.subRows.length > 1;
        const productCategoryId =
          row.original.activeVersion.productCategory?.productCategoryId ?? null;

        const errorState = getTableCellErrorState({
          row,
          field: 'productCategory',
          errorType: 'empty',
        });

        const displayValue = isParent
          ? getParentValue(row, categoryTreeForSorting)
          : getValue(row, categoryTreeForSorting);

        return (
          <CategoryCell
            row={row}
            dealId={dealId}
            isOfferLocked={isOfferLocked}
            categoryTree={categoryTreeForSorting}
            displayValue={displayValue}
            productCategoryId={productCategoryId}
            isEmpty={errorState.isEmpty}
            isParent={isParent}
          />
        );
      },
      size: 180,
    }),
    [t, isOfferLocked, setColumnSort, categoryTreeForSorting, dealId, filterFn]
  );
};

export default useColumnDefType;
